import React, { useState } from 'react'
import { Post, Emoji, getResourceUrl } from '../../../services/api'
import { PlayIcon } from '@heroicons/react/24/solid'
import { MapPinIcon } from '@heroicons/react/24/outline'
import { AudioPlayer } from '../../../components/AudioPlayer/AudioPlayer'
import { NavigationArrow } from '../../../components/NavigationArrow/NavigationArrow'
import { UserAvatar } from '../../../components/Common/UserAvatar'
import { EmojiText } from './EmojiText'
import { EmojiMarkdown } from './EmojiMarkdown'

interface PostCardProps {
  post: Post
  /** Custom workspace emojis keyed by name. */
  emojis?: Map<string, Emoji>
  /** Called when user clicks an image; receives the resource index within the post. */
  onImageClick?: (resourceIndex: number) => void
}

const formatRelativeDate = (dateStr: string): string => {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMinutes < 1) return 'teraz'
  if (diffMinutes < 60) return `${diffMinutes} min temu`
  if (diffHours < 24) return `${diffHours} godz. temu`
  if (diffDays === 1) return 'wczoraj'
  if (diffDays < 30) return `${diffDays} dni temu`
  return date.toLocaleDateString('pl-PL')
}

/** Returns true when the hex colour is dark (text on it should be white). */
const isColorDark = (hex: string): boolean => {
  const c = hex.replace('#', '')
  if (c.length < 6) return false
  const r = parseInt(c.substring(0, 2), 16)
  const g = parseInt(c.substring(2, 4), 16)
  const b = parseInt(c.substring(4, 6), 16)
  // W3C relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance < 0.55
}

const getAuthorName = (author?: { first_name?: string; last_name?: string }): string => {
  if (!author) return 'Nieznany'
  const name = [author.first_name, author.last_name].filter(Boolean).join(' ')
  return name || 'Nieznany'
}

const getAuthorInitials = (author?: { first_name?: string; last_name?: string }): string => {
  if (!author) return '?'
  return (author.first_name?.[0] || author.last_name?.[0] || '?').toUpperCase()
}

const EMPTY_EMOJIS = new Map<string, Emoji>()

export const PostCard: React.FC<PostCardProps> = ({ post, emojis = EMPTY_EMOJIS, onImageClick }) => {
  const [showAllComments, setShowAllComments] = useState(false)
  const [mediaIndex, setMediaIndex] = useState(0)

  const images = post.resources.filter((r) => r.type === 'image')
  const videos = post.resources.filter((r) => r.type === 'video')
  const hasMedia = images.length > 0 || videos.length > 0
  const mediaCount = images.length + videos.length
  const firstResource = post.resources[0]

  const authorName = getAuthorName(post.author)
  const authorInitials = getAuthorInitials(post.author)

  const visibleComments = showAllComments
    ? post.comments
    : post.comments?.slice(0, 2)

  return (
    <div className="flex flex-col overflow-hidden rounded-xl bg-white border border-gray-200">
      {/* Title */}
      {post.title && (
        <div
          className="px-4 py-3 text-center"
          style={{ backgroundColor: post.color || '#fde68a' }}
        >
          <h3
            className="text-lg font-bold leading-snug"
            style={{ color: isColorDark(post.color) ? '#fff' : '#111827' }}
          >
            <EmojiText text={post.title} emojis={emojis} />
          </h3>
        </div>
      )}

      {/* Content / description */}
      {post.content && (
        <div className="px-4 py-2 text-sm text-gray-600 prose prose-sm max-w-none prose-hr:my-2">
          <EmojiMarkdown emojis={emojis}>{post.content}</EmojiMarkdown>
        </div>
      )}

      {/* Recording / Audio player */}
      {post.recording && (
        <AudioPlayer
          src={`${post.recording.url}?signature=${post.recording.signature}`}
          className="px-4 py-2"
        />
      )}

      {/* Media gallery */}
      {hasMedia && firstResource && (
        <div className="group relative aspect-square w-full overflow-hidden">
          <img
            src={getResourceUrl(post.resources[mediaIndex] || firstResource, 'large')}
            alt={post.title || 'Post image'}
            className="h-full w-full object-cover cursor-zoom-in"
            onClick={() => onImageClick?.(mediaIndex)}
          />
          {/* Video play overlay */}
          {(post.resources[mediaIndex] || firstResource).type === 'video' && (
            <div
              className="absolute inset-0 flex items-center justify-center cursor-pointer"
              onClick={() => onImageClick?.(mediaIndex)}
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-black/40 backdrop-blur-sm transition-transform hover:scale-110">
                <PlayIcon className="h-7 w-7 text-white ml-0.5" />
              </div>
            </div>
          )}
          {/* Navigation arrows — visible on hover when multiple resources */}
          {mediaCount > 1 && mediaIndex > 0 && (
            <NavigationArrow
              direction="left"
              size="sm"
              onClick={() => setMediaIndex((i) => i - 1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 !bg-black/40 hover:!bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity"
            />
          )}
          {mediaCount > 1 && mediaIndex < mediaCount - 1 && (
            <NavigationArrow
              direction="right"
              size="sm"
              onClick={() => setMediaIndex((i) => i + 1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 !bg-black/40 hover:!bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity"
            />
          )}
          {/* Media count badge */}
          {mediaCount > 1 && (
            <div className="absolute bottom-3 left-3 flex h-7 min-w-7 items-center justify-center rounded-full bg-amber-100 px-2 text-xs font-semibold text-gray-800 shadow">
              {mediaIndex + 1}/{mediaCount}
            </div>
          )}
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-gray-200" />

      {/* Author row */}
      <div className="flex items-center gap-2 px-4 py-2.5">
        <UserAvatar
          size="xs"
          initials={authorInitials}
          imageUrl={post.author?.avatar}
          color={post.author?.color}
        />
        <span className="text-sm font-medium text-gray-800">{authorName}</span>
        {post.location && (
          <>
            <span className="text-xs text-gray-400">z</span>
            <MapPinIcon className="h-3 w-3 text-gray-400 -mr-1" />
            <span className="text-xs text-gray-500 truncate max-w-[200px]">
              {post.location}
            </span>
          </>
        )}
      </div>

      {/* Reactions row */}
      {post.reactions.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-4 pb-2">
          {post.reactions.map((r, i) => {
            const customEmoji = emojis.get(r.reaction)
            return (
              <div
                key={`${r.reaction}-${i}`}
                className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-sm"
              >
                {customEmoji ? (
                  <img
                    src={customEmoji.image_url}
                    alt={`:${r.reaction}:`}
                    title={`:${r.reaction}:`}
                    className="h-5 w-5 object-contain"
                  />
                ) : (
                  <span>{r.reaction}</span>
                )}
                <span className="text-xs text-gray-500">{r.users.length}</span>
            </div>
            )
          })}
        </div>
      )}

      {/* Comments */}
      {post.comments && post.comments.length > 0 && (
        <div className="border-t border-gray-100 px-4 py-2 space-y-1.5">
          {visibleComments?.map((comment) => {
            const commentAuthor = getAuthorName(comment.author)

            return (
              <div key={comment.id} className="flex flex-col">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-semibold text-gray-800">
                    {commentAuthor}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {formatRelativeDate(comment.created_at)}
                  </span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {comment.content}
                </p>
              </div>
            )
          })}
          {post.comments.length > 2 && !showAllComments && (
            <button
              onClick={() => setShowAllComments(true)}
              className="text-xs text-indigo-500 hover:text-indigo-600 font-medium"
            >
              Pokaż wszystkie ({post.comments.length})
            </button>
          )}
        </div>
      )}
    </div>
  )
}
